import React, { useEffect, useRef, useState } from 'react';
import { Button, TwIconButton } from 'components/common';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FaTimes, FaLink, FaClone, FaCheck, FaGlobe } from 'react-icons/fa';
import { getFileMeta } from './fileTypes';
const DriveGetLinkModal = ({ show, onClose, asset }) => {
  const inputRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const isFolder = asset?.asset_type === 'folder';
  const url = asset
    ? (isFolder ? window.location.href : (asset.preview_url || window.location.href))
    : '';

  const { icon: Icon, color } = asset ? getFileMeta(asset) : { icon: FaLink, color: '#64748b' };

  useEffect(() => {
    if (!show) return undefined;
    setCopied(false);
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // auto-select link content on open
    setTimeout(() => {
      inputRef.current?.select();
    }, 80);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [show, onClose]);

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error('Failed to copy: ' + (e?.message || 'unknown'));
    }
  };

  if (!show || !asset) return null;

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 999999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        backgroundColor: 'rgba(15,23,42,0.45)',
        animation: 'driveLinkFadeIn 180ms ease',
      }}
    >
      <style>{`
        @keyframes driveLinkFadeIn { from { background-color: rgba(15,23,42,0); } to { background-color: rgba(15,23,42,0.45); } }
        @keyframes driveLinkPopIn { from { transform: translateY(8px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460,
          backgroundColor: '#ffffff',
          borderRadius: 14,
          boxShadow: '0 20px 60px rgba(15,23,42,0.18), 0 4px 12px rgba(15,23,42,0.08)',
          overflow: 'hidden',
          animation: 'driveLinkPopIn 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#eff6ff', color: '#2563eb',
            border: '1px solid #dbeafe', borderRadius: 8,
          }}>
            <FaLink size={13} />
          </div>
          <h3 style={{
            flex: 1, margin: 0,
            fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.4,
          }}>
            Get link
          </h3>
          <TwIconButton icon={FaTimes} size="md" variant="ghost" onClick={onClose} title="Close" />
        </div>

        {/* Body */}
        <div style={{ padding: '16px' }}>
          {/* Asset preview */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
          }}>
            <div style={{
              width: 36, height: 36, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: '#ffffff', border: '1px solid #f1f5f9',
              borderRadius: 8,
            }}>
              <Icon size={18} style={{ color }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: 0, fontSize: 13, fontWeight: 700, color: '#0f172a',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.4,
              }} title={asset.name}>
                {asset.name}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                {isFolder ? 'Folder' : (asset.content_type || 'File')}
              </p>
            </div>
          </div>

          {/* Access hint */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginTop: 12,
            fontSize: 11, color: '#64748b', fontWeight: 600, lineHeight: 1.4,
          }}>
            <FaGlobe size={9} style={{ color: '#94a3b8' }} />
            <span>Anyone with this link can {isFolder ? 'view this folder' : 'view this file'}.</span>
          </div>

          {/* Link input + copy */}
          <div style={{
            display: 'flex', alignItems: 'stretch', gap: 8,
            marginTop: 10,
          }}>
            <input
              ref={inputRef}
              type="text"
              readOnly
              value={url}
              onClick={(e) => e.currentTarget.select()}
              style={{
                appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
                flex: 1, minWidth: 0, height: 38, margin: 0,
                padding: '0 12px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 12, fontWeight: 500,
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                color: '#0f172a',
                outline: 'none',
                lineHeight: 1.4,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#0f172a'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
            />
            <Button
              variant={copied ? 'info' : 'secondary'}
              size="md"
              leftIcon={copied ? FaCheck : FaClone}
              onClick={handleCopy}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

DriveGetLinkModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  asset: PropTypes.object,
};

export default DriveGetLinkModal;
